import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  AdvancedFilterType,
  createAdvancedFilterDataSchema,
  GenericFilterBuilder,
} from "./generic-advanced-filter-data";

export enum BooleanFilterOperator {
  IS = "is",
  IS_NOT = "is_not",
}
const BooleanFilterOperatorSchema = createEnum(BooleanFilterOperator, {
  enumName: "BooleanFilterOperator",
}).meta({ privacy: privacy.enumOrStatus });

const BooleanFilterValueSchema = z.boolean();

export const BooleanFilterDataSchema = createAdvancedFilterDataSchema(
  AdvancedFilterType.BOOLEAN,
  BooleanFilterValueSchema,
  BooleanFilterOperatorSchema,
);
export type BooleanFilterData = z.infer<typeof BooleanFilterDataSchema>;

export const BooleanFilterBuilder: GenericFilterBuilder<
  boolean,
  BooleanFilterOperator,
  BooleanFilterData
> = {
  type: AdvancedFilterType.BOOLEAN,
  valueSchema: BooleanFilterValueSchema,
  operatorSchema: BooleanFilterOperatorSchema,
  schema: BooleanFilterDataSchema,
  addOpensearchFilters: ({ filter, opensearchPath, compound }) => {
    const { value } = filter;

    if (value == null) {
      return;
    }

    // the below code assumes that !exists is equivalent to false
    if (
      (filter.operator === BooleanFilterOperator.IS && value === true) ||
      (filter.operator === BooleanFilterOperator.IS_NOT && value === false)
    ) {
      compound.filter.push({ term: { [opensearchPath]: true } });
    }

    if (
      (filter.operator === BooleanFilterOperator.IS_NOT && value === true) ||
      (filter.operator === BooleanFilterOperator.IS && value === false)
    ) {
      compound.must_not.push({ term: { [opensearchPath]: true } });
    }
  },
  readFromString: (name, operatorValueEncoded, defaultFilter) => {
    try {
      const decoded = decodeURIComponent(operatorValueEncoded);
      const operatorValue = decoded.split("::");

      const operator = operatorValue?.[0];
      const value = operatorValue?.[1];

      if (!operator) {
        return defaultFilter;
      }

      const parsedOperator = BooleanFilterOperatorSchema.safeParse(operator);
      if (!parsedOperator.success) {
        console.warn("Invalid operator type");
        return undefined;
      }

      if (value == null) {
        return {
          type: AdvancedFilterType.BOOLEAN,
          name,
          value: null,
          operator: parsedOperator.data,
        };
      }

      const valueParsed = BooleanFilterValueSchema.safeParse(
        value.trim() === "true",
      );

      if (!valueParsed.success) {
        console.warn("Invalid value type for BooleanFilter");
        return undefined;
      }

      return {
        type: AdvancedFilterType.BOOLEAN,
        name,
        value: valueParsed.data,
        operator: parsedOperator.data,
      };
    } catch (error) {
      console.error("Error parsing BooleanFilter", error);
      return undefined;
    }
  },
  writeToString: (filter: BooleanFilterData | undefined) => {
    try {
      if (!filter) {
        return "";
      }
      const { operator, value } = filter;
      const parsedValue = BooleanFilterValueSchema.safeParse(value);

      if (parsedValue.success) {
        return `${operator}::${parsedValue.data}`;
      } else {
        return `${operator}::`;
      }
    } catch (error) {
      console.error("Error writing BooleanFilter", error);
      return "";
    }
  },
  isPartial: (filter) => {
    return !filter.operator || filter.value == null;
  },
};
